import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  ChevronLeft, ChevronRight, Target, TrendingUp, Users, AlertTriangle, 
  CheckCircle, Trophy, BookOpen, BarChart3, Lightbulb, ArrowRight,
  Star, Shield, Zap, DollarSign
} from 'lucide-react';

interface ValidationData {
  idea: {
    title: string;
    description: string;
    target_market: string;
  };
  swot: {
    strengths: string[];
    weaknesses: string[];
    opportunities: string[];
    threats: string[];
  };
  critical_questions: Array<{
    question: string;
    importance: 'high' | 'medium' | 'low';
  }>;
  competition: Array<{
    name: string;
    description: string;
    differentiators: string[];
  }>;
  action_plan: Array<{
    step: string;
    timeline: string;
    priority: 'high' | 'medium' | 'low';
    completed?: boolean;
  }>;
  market_demand: {
    size: 'large' | 'medium' | 'small';
    growth: 'high' | 'medium' | 'low';
    indicators: string[];
  };
  frameworks: {
    lean_startup: string;
    customer_development: string;
    mvp: string;
  };
}

interface FullPageValidationProps {
  data: ValidationData;
  onBack?: () => void;
}

export const FullPageValidation: React.FC<FullPageValidationProps> = ({
  data,
  onBack
}) => {
  const [currentTab, setCurrentTab] = useState(0);

  const tabs = [
    { id: 'swot', label: 'SWOT Analysis', icon: Target },
    { id: 'questions', label: 'Critical Questions', icon: Lightbulb },
    { id: 'mvp', label: 'Build Your MVP', icon: BookOpen },
    { id: 'market', label: 'Reach Target Market', icon: Users },
    { id: 'competition', label: 'Competition', icon: Trophy },
    { id: 'action', label: 'Action Plan', icon: CheckCircle },
    { id: 'demand', label: 'Market Demand', icon: BarChart3 },
    { id: 'frameworks', label: 'Frameworks', icon: BookOpen },
  ];

  const nextTab = () => {
    if (currentTab < tabs.length - 1) {
      setCurrentTab(currentTab + 1);
    }
  };

  const prevTab = () => {
    if (currentTab > 0) {
      setCurrentTab(currentTab - 1);
    }
  };

  const getSWOTColor = (type: 'strengths' | 'weaknesses' | 'opportunities' | 'threats') => {
    switch (type) {
      case 'strengths': return 'bg-blue-50 border-blue-200 text-blue-800';
      case 'weaknesses': return 'bg-orange-50 border-orange-200 text-orange-800';
      case 'opportunities': return 'bg-green-50 border-green-200 text-green-800';
      case 'threats': return 'bg-red-50 border-red-200 text-red-800';
    }
  };

  const getSWOTIcon = (type: 'strengths' | 'weaknesses' | 'opportunities' | 'threats') => {
    switch (type) {
      case 'strengths': return <Star className="w-5 h-5 text-blue-600" />;
      case 'weaknesses': return <AlertTriangle className="w-5 h-5 text-orange-600" />;
      case 'opportunities': return <TrendingUp className="w-5 h-5 text-green-600" />;
      case 'threats': return <Shield className="w-5 h-5 text-red-600" />;
    }
  };

  const getPriorityColor = (priority: 'high' | 'medium' | 'low') => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800 border-red-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low': return 'bg-green-100 text-green-800 border-green-200';
    }
  };

  const renderTabContent = () => {
    const currentTabData = tabs[currentTab];
    
    switch (currentTabData.id) {
      case 'swot':
        return (
          <div className="space-y-8">
            <div className="text-center">
              <h2 className="text-4xl font-bold mb-4">SWOT Analysis</h2>
              <p className="text-xl text-muted-foreground">Analyze your startup's strengths, weaknesses, opportunities, and threats</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Strengths */}
              <Card className={getSWOTColor('strengths')}>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2 text-xl">
                    {getSWOTIcon('strengths')}
                    <span>✨ STRENGTHS</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {data.swot.strengths.map((strength, index) => (
                      <li key={index} className="flex items-start space-x-2">
                        <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-base">{strength}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              {/* Weaknesses */}
              <Card className={getSWOTColor('weaknesses')}>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2 text-xl">
                    {getSWOTIcon('weaknesses')}
                    <span>⚠️ WEAKNESSES</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {data.swot.weaknesses.map((weakness, index) => (
                      <li key={index} className="flex items-start space-x-2">
                        <div className="w-2 h-2 bg-orange-600 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-base">{weakness}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              {/* Opportunities */}
              <Card className={getSWOTColor('opportunities')}>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2 text-xl">
                    {getSWOTIcon('opportunities')}
                    <span>🚀 OPPORTUNITIES</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {data.swot.opportunities.map((opportunity, index) => (
                      <li key={index} className="flex items-start space-x-2">
                        <div className="w-2 h-2 bg-green-600 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-base">{opportunity}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              {/* Threats */}
              <Card className={getSWOTColor('threats')}>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2 text-xl">
                    {getSWOTIcon('threats')}
                    <span>🔴 THREATS</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {data.swot.threats.map((threat, index) => (
                      <li key={index} className="flex items-start space-x-2">
                        <div className="w-2 h-2 bg-red-600 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-base">{threat}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        );
      
      case 'questions':
        return (
          <div className="space-y-8">
            <div className="text-center">
              <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Lightbulb className="w-10 h-10 text-blue-600" />
              </div>
              <h2 className="text-4xl font-bold mb-4">Critical <span className="text-blue-600">Questions</span></h2>
              <p className="text-xl text-muted-foreground">Essential questions to deeply understand and refine your startup idea</p>
            </div>
            <div className="space-y-6">
              {data.critical_questions.map((item, index) => (
                <Card key={index} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-8">
                    <div className="flex items-start space-x-6">
                      <div className="w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center text-lg font-bold flex-shrink-0">
                        {index + 1}
                      </div>
                      <div className="flex-1">
                        <p className="text-gray-800 font-medium text-xl mb-3">{item.question}</p>
                        <Badge className={`${getPriorityColor(item.importance)}`}>
                          {item.importance} priority
                        </Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        );
        
      case 'mvp':
        return (
          <div className="space-y-8">
            <div className="text-center">
              <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <BookOpen className="w-10 h-10 text-purple-600" />
              </div>
              <h2 className="text-4xl font-bold mb-4">Build Your <span className="text-blue-600">MVP</span></h2>
              <p className="text-xl text-muted-foreground">Essential steps to create your Minimum Viable Product and validate your idea in the market.</p>
            </div>
            
            <Card>
              <CardContent className="p-8">
                <div className="mb-8">
                  <h3 className="text-2xl font-semibold mb-4 text-center">MVP Strategy</h3>
                  <p className="text-lg text-gray-600 mb-8 text-center max-w-3xl mx-auto">{data.frameworks.mvp}</p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  <Card className="border-2 border-blue-200 hover:border-blue-400 transition-colors">
                    <CardContent className="p-6 text-center">
                      <div className="w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                        1
                      </div>
                      <h4 className="text-xl font-semibold mb-3">Core Features</h4>
                      <p className="text-gray-600">Identify the minimum set of features needed to solve the core problem for your target audience</p>
                    </CardContent>
                  </Card>
                  
                  <Card className="border-2 border-green-200 hover:border-green-400 transition-colors">
                    <CardContent className="p-6 text-center">
                      <div className="w-12 h-12 bg-green-600 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                        2
                      </div>
                      <h4 className="text-xl font-semibold mb-3">Build & Test</h4>
                      <p className="text-gray-600">Create a simple version and test with early users to validate assumptions</p>
                    </CardContent>
                  </Card>
                  
                  <Card className="border-2 border-orange-200 hover:border-orange-400 transition-colors">
                    <CardContent className="p-6 text-center">
                      <div className="w-12 h-12 bg-orange-600 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                        3
                      </div>
                      <h4 className="text-xl font-semibold mb-3">Learn & Iterate</h4>
                      <p className="text-gray-600">Gather feedback and improve based on user insights and market response</p>
                    </CardContent>
                  </Card>
                </div>
                
                <div className="mt-8 p-6 bg-blue-50 rounded-lg">
                  <h4 className="text-lg font-semibold mb-3 text-blue-800">💡 MVP Tips for Your Idea:</h4>
                  <ul className="space-y-2 text-blue-700">
                    <li className="flex items-start space-x-2">
                      <div className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 flex-shrink-0"></div>
                      <span>Start with solving the core problem: {data.idea.description.split('.')[0]}</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <div className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 flex-shrink-0"></div>
                      <span>Focus on your target market: {data.idea.target_market}</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <div className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 flex-shrink-0"></div>
                      <span>Launch quickly and iterate based on user feedback</span>
                    </li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </div>
        );
        
      case 'market':
        return (
          <div className="space-y-8">
            <div className="text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Users className="w-10 h-10 text-green-600" />
              </div>
              <h2 className="text-4xl font-bold mb-4">Reach Your <span className="text-blue-600">Target Market</span></h2>
              <p className="text-xl text-muted-foreground">Strategic approaches to connect with: <strong>{data.idea.target_market}</strong></p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[
                { title: "Content Marketing", description: "Create valuable content that attracts your target audience", color: "bg-blue-500" },
                { title: "Social Media", description: "Engage with potential customers on relevant platforms", color: "bg-purple-500" },
                { title: "Direct Outreach", description: "Personally reach out to potential customers", color: "bg-green-500" },
                { title: "Partnerships", description: "Collaborate with complementary businesses", color: "bg-orange-500" },
                { title: "Paid Advertising", description: "Use targeted ads to reach specific demographics", color: "bg-red-500" }
              ].map((method, index) => (
                <Card key={index} className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-8 text-center">
                    <div className={`w-16 h-16 ${method.color} text-white rounded-full flex items-center justify-center text-2xl font-bold mb-6 mx-auto`}>
                      {index + 1}
                    </div>
                    <h3 className="text-xl font-semibold mb-3">{method.title}</h3>
                    <p className="text-gray-600">{method.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        );
        
      case 'competition':
        return (
          <div className="space-y-8">
            <div className="text-center">
              <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Trophy className="w-10 h-10 text-yellow-600" />
              </div>
              <h2 className="text-4xl font-bold mb-4">Competition Analysis</h2>
              <p className="text-xl text-muted-foreground">Understanding your competitive landscape and positioning strategy</p>
            </div>
            
            <div className="space-y-6">
              {data.competition.map((competitor, index) => (
                <Card key={index} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-8">
                    <div className="flex items-start justify-between mb-6">
                      <div>
                        <h4 className="text-2xl font-semibold text-gray-900 mb-2">{competitor.name}</h4>
                        <p className="text-lg text-gray-600">{competitor.description}</p>
                      </div>
                      <Badge variant="outline" className="text-lg px-3 py-1">
                        #{index + 1}
                      </Badge>
                    </div>
                    <div>
                      <h5 className="text-lg font-medium text-gray-700 mb-3">🟢 Your Differentiators:</h5>
                      <ul className="space-y-2">
                        {competitor.differentiators.map((diff, diffIndex) => (
                          <li key={diffIndex} className="text-gray-600 flex items-start space-x-2">
                            <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                            <span className="text-base">{diff}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        );
        
      case 'action':
        return (
          <div className="space-y-8">
            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-4xl font-bold mb-4">Your <span className="text-blue-600">Action Plan</span></h2>
              <p className="text-xl text-muted-foreground">Actionable steps to move from concept to reality and reach 100K MRR 💰</p>
            </div>
            
            <div className="space-y-6">
              {data.action_plan.map((step, index) => (
                <Card key={index} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-8">
                    <div className="flex items-start space-x-6">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                        step.completed ? 'bg-green-500' : 'bg-blue-500'
                      }`}>
                        {step.completed ? (
                          <CheckCircle className="w-6 h-6 text-white" />
                        ) : (
                          <span className="text-white text-lg font-bold">{index + 1}</span>
                        )}
                      </div>
                      <div className="flex-1">
                        <p className={`font-medium text-lg mb-3 ${
                          step.completed ? 'line-through text-gray-500' : 'text-gray-800'
                        }`}>
                          {step.step}
                        </p>
                        <div className="flex items-center space-x-3">
                          <Badge variant="outline">
                            {step.timeline}
                          </Badge>
                          <Badge className={getPriorityColor(step.priority)}>
                            {step.priority} priority
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        );
        
      case 'demand':
        return (
          <div className="space-y-8">
            <div className="text-center">
              <div className="w-20 h-20 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <BarChart3 className="w-10 h-10 text-indigo-600" />
              </div>
              <h2 className="text-4xl font-bold mb-4">Market <span className="text-blue-600">Demand Indicators</span></h2>
              <p className="text-xl text-muted-foreground">Key signals that indicate market demand for your solution</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
              <Card className="text-center">
                <CardContent className="p-8">
                  <h3 className="text-xl font-semibold mb-4">Market Size</h3>
                  <div className={`inline-block px-4 py-2 rounded-full text-lg font-medium ${
                    data.market_demand.size === 'large' ? 'bg-green-100 text-green-800' :
                    data.market_demand.size === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {data.market_demand.size.toUpperCase()}
                  </div>
                </CardContent>
              </Card>
              
              <Card className="text-center">
                <CardContent className="p-8">
                  <h3 className="text-xl font-semibold mb-4">Growth Rate</h3>
                  <div className={`inline-block px-4 py-2 rounded-full text-lg font-medium ${
                    data.market_demand.growth === 'high' ? 'bg-green-100 text-green-800' :
                    data.market_demand.growth === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {data.market_demand.growth.toUpperCase()}
                  </div>
                </CardContent>
              </Card>
              
              <Card className="text-center">
                <CardContent className="p-8">
                  <h3 className="text-xl font-semibold mb-4">Overall Outlook</h3>
                  <div className="text-2xl mb-2">
                    {data.market_demand.size === 'large' && data.market_demand.growth === 'high' ? '🚀' :
                     data.market_demand.size === 'medium' || data.market_demand.growth === 'medium' ? '📈' : '⚠️'}
                  </div>
                  <p className="text-sm text-gray-600">
                    {data.market_demand.size === 'large' && data.market_demand.growth === 'high' ? 'Excellent potential' :
                     data.market_demand.size === 'medium' || data.market_demand.growth === 'medium' ? 'Good opportunity' : 'Proceed with caution'}
                  </p>
                </CardContent>
              </Card>
            </div>
            
            <Card>
              <CardContent className="p-8">
                <h3 className="text-xl font-semibold mb-6">📊 Market Indicators</h3>
                <ul className="space-y-4">
                  {data.market_demand.indicators.map((indicator, index) => (
                    <li key={index} className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-indigo-600 rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-base text-gray-700">{indicator}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        );
        
      case 'frameworks':
        return (
          <div className="space-y-8">
            <div className="text-center">
              <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <BookOpen className="w-10 h-10 text-purple-600" />
              </div>
              <h2 className="text-4xl font-bold mb-4"><span className="text-blue-600">Frameworks</span> to Evaluate</h2>
              <p className="text-xl text-muted-foreground">Strategic frameworks to critically assess and enhance your startup's approach</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <Card className="hover:shadow-lg transition-shadow">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Zap className="w-8 h-8 text-white" />
                  </div>
                  <h4 className="text-xl font-semibold text-gray-900 mb-4">Lean Startup Methodology</h4>
                  <p className="text-gray-600">{data.frameworks.lean_startup}</p>
                </CardContent>
              </Card>
              
              <Card className="hover:shadow-lg transition-shadow">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Users className="w-8 h-8 text-white" />
                  </div>
                  <h4 className="text-xl font-semibold text-gray-900 mb-4">Customer Development</h4>
                  <p className="text-gray-600">{data.frameworks.customer_development}</p>
                </CardContent>
              </Card>
              
              <Card className="hover:shadow-lg transition-shadow">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 bg-purple-500 rounded-full flex items-center justify-center mx-auto mb-6">
                    <BookOpen className="w-8 h-8 text-white" />
                  </div>
                  <h4 className="text-xl font-semibold text-gray-900 mb-4">Minimum Viable Product (MVP)</h4>
                  <p className="text-gray-600">{data.frameworks.mvp}</p>
                </CardContent>
              </Card>
            </div>
          </div>
        );
        
      default:
        return (
          <div className="text-center py-20">
            <h2 className="text-3xl font-bold mb-4">{currentTabData.label}</h2>
            <p className="text-muted-foreground">Content for this section is being developed.</p>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-teal-50">
      {/* Header */}
      <div className="bg-white border-b shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {onBack && (
                <Button variant="outline" onClick={onBack} className="flex items-center space-x-2">
                  <ChevronLeft className="w-4 h-4" />
                  <span>Back to Validation</span>
                </Button>
              )}
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                  <Target className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-gray-900">Validation Results</h1>
                  <p className="text-sm text-gray-500">Step {currentTab + 1} of {tabs.length}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex space-x-1 overflow-x-auto">
            {tabs.map((tab, index) => (
              <button
                key={tab.id}
                onClick={() => setCurrentTab(index)}
                className={`flex items-center space-x-2 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                  currentTab === index
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        {renderTabContent()}
      </div>

      {/* Navigation Footer */}
      <div className="bg-white border-t sticky bottom-0">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              onClick={prevTab}
              disabled={currentTab === 0}
              className="flex items-center space-x-2"
            >
              <ChevronLeft className="w-4 h-4" />
              <span>Previous</span>
            </Button>
            
            <div className="flex items-center space-x-2">
              {tabs.map((_, index) => (
                <div
                  key={index}
                  className={`w-2 h-2 rounded-full ${
                    index === currentTab ? 'bg-blue-600' : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>

            <Button
              onClick={nextTab}
              disabled={currentTab === tabs.length - 1}
              className="flex items-center space-x-2"
            >
              <span>Next</span>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
